import React, { Component } from 'react';
import { orderBy, get, isNumber, find } from 'lodash';
import { arrayOf, shape, string, number, func, bool, objectOf } from 'prop-types';
import { Tooltip } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';
import BaseDataTable from '../../../BaseDataTable';
import TableHead from './ChannelsTableHead';
import TableRow from './ChannelsTableRow';
import TableSummary from './ChannelsTableSummary';
import ModuleExport from '../../../ModuleExport';
import styles from './styles.scss';
import {
  MIN_LENGTH_TRAFFIC_CHANNEL_DATA,
  SORT_BY_VISIT_PER_AD,
  TRAFFIC_CHANNEL_CHART_ID,
    } from '../../../../helpers/constant';
import { ADSICON_TOOLTIP_TEXT } from '../../constants';
import { calculateTableRows } from '../../Report/helper';
import { INIT_AD_VISITS_SORT } from 'components/DashboardTab/ChannelsAnalytics/constants';

export class ChannelsTable extends Component {
  static propTypes = {
    data: arrayOf(shape({
      trafficId: string,
      trafficType: string,
      adVisits: number,
      adUsers: number,
      ads: number,
      avgAdCost: number
    })).isRequired,
    totalAds: number,
    avgAdCost: number,
    onCellClick: func,
    fileName: string,
    inpdf: bool,
    page: number,
    rowsPerPage: number,
    isGrandTotalDisplay: bool,
    hideChannelFields: bool,
    isCustomReport: bool,
    isDisplayChart: bool,
    itemIndex: number,
    tableSortSelector: shape({
      sortKey: string,
      sortType: string,
    }),
    tableColumns: objectOf(shape({
      title: string,
      key: string
    })),
    csvColumns: objectOf(shape({
      title: string,
      key: string
    })),
    isNewAdUsersSelected: bool,
    rowsFirstPage: number
  }

  static defaultProps = {
    tableColumns: {},
    fileName: 'traffic_channel_analytics_table'
  }

  prepData() {
    const {
      data,
      totalAds,
      avgAdCost: sumAvgAdCost,
      inpdf,
      page,
      rowsPerPage,
      isCustomReport,
      isDisplayChart,
      tableSortSelector,
      isNewAdUsersSelected,
      rowsFirstPage
    } = this.props;
    const dataSet = data && data.reduce((accum, currEntry) => {
      const {
        trafficId,
        trafficType,
        adVisits = 0,
        ads = 0,
        adUsers = 0,
        avgAdCost = 'N/A',
        newAdUsers = 0
      } = currEntry;
      if ( trafficId && trafficType ) {
        const visitOfAds = ads && parseFloat((adVisits / ads).toFixed(2)) || 0;
        const newAdUsersOfAds = ads && parseFloat((newAdUsers / ads).toFixed(2)) || 0;
        accum.tableData.push({
          trafficId,
          trafficType,
          adVisits,
          adUsers,
          ads,
          visitOfAds,
          avgAdCost,
          newAdUsers,
          newAdUsersOfAds
        });
      }
      accum.summaryData.adVisits += adVisits;
      accum.summaryData.newAdUsers += newAdUsers;
      accum.summaryData.adUsers += adUsers;
      accum.summaryData.ads += ads;
      return accum;
    }, {
      tableData:[],
      summaryData: {
        adVisits: 0,
        newAdUsers: 0,
        adUsers: 0,
        ads: 0,
        uniqueAds: totalAds,
        avgAdCost: sumAvgAdCost
      }});
    dataSet.summaryData.visitOfAds = dataSet.summaryData.ads && parseFloat((dataSet.summaryData.adVisits / dataSet.summaryData.ads).toFixed(2)) || 0;
    dataSet.summaryData.newAdUsersOfAds = dataSet.summaryData.ads && parseFloat((dataSet.summaryData.newAdUsers / dataSet.summaryData.ads).toFixed(2)) || 0;
     //TODO: update this when new report is ready
    if ( inpdf ) {
      const defaultSortStatus = isNewAdUsersSelected ? INIT_NEW_AD_USERS_PER_AD_SORT : SORT_BY_VISIT_PER_AD;
      const initialSortStatus = tableSortSelector || defaultSortStatus;
      const { sortKey, sortType } = initialSortStatus || {};
      dataSet.tableData = orderBy(dataSet.tableData, [sortKey], sortType);
      const { tableRowsFirstPage, tableRowsOtherPage } = calculateTableRows({ displayChart: isDisplayChart, isCustomReport, minRows:rowsFirstPage || MIN_LENGTH_TRAFFIC_CHANNEL_DATA, truncatedRows: 0 });
      if ( page === 0) {
        dataSet.tableData = dataSet.tableData.slice(0, rowsPerPage);
      } else {
        const startIndex = (tableRowsFirstPage + 1) + (page - 1) * (tableRowsOtherPage + 1);
        dataSet.tableData = dataSet.tableData.slice(startIndex, startIndex + rowsPerPage );
      }
    }
    return dataSet;
  }

  handleCellClick = ({data}) => {
    const { onCellClick } = this.props;
    onCellClick && onCellClick(data);
  }

  prepCsvData(data, summaryData) {
    const { csvColumns } = this.props;
    const headers = Object.values(csvColumns).map(item => item.title);
    const totalAdVisits = get(summaryData, 'adVisits');
    const totalNewAdUsers = get(summaryData, 'newAdUsers');
    const csvData = data.reduce((acc, current)=> {
      const {
        trafficType,
        adVisits,
        newAdUsers,
        ads,
        visitOfAds,
        newAdUsersOfAds,
        avgAdCost
      } = current;
      const adVisitsRate = totalAdVisits && parseFloat((adVisits / totalAdVisits).toFixed(4)) || 0;
      const newAdUsersRate = totalNewAdUsers && parseFloat((newAdUsers / totalNewAdUsers).toFixed(4)) || 0;
      const formattedAvgAdCost = isNumber(avgAdCost) ? avgAdCost.toFixed(2) : 'N/A';
      const formattedData = {
        trafficType,
        adVisits,
        newAdUsers,
        ads,
        visitOfAds,
        newAdUsersOfAds,
        adVisitsRate,
        newAdUsersRate,
        avgAdCost: formattedAvgAdCost,
      }
      const row = Object.keys(csvColumns).map(item => formattedData[item])
      acc.push(row);
      return acc;
    },[ headers ])
    return csvData;
  }
  /* Find the init sort base on displayed columns*/
  get initialSortStatus() {
    const { tableSortSelector, tableColumns, isNewAdUsersSelected, hideChannelFields } = this.props;
    const initSortWhenNewAdUsersSelected = hideChannelFields ? INIT_NEW_AD_USERS_SORT : INIT_NEW_AD_USERS_PER_AD_SORT; 
    const initSortPriority = [
      isNewAdUsersSelected ? initSortWhenNewAdUsersSelected : SORT_BY_VISIT_PER_AD,
      INIT_AD_VISITS_SORT
    ];
    if (tableSortSelector) {
      initSortPriority.unshift(tableSortSelector);
    }
    return find(initSortPriority, sortObj => {
      const sortKey = get(sortObj, 'sortKey')
      return tableColumns[sortKey];
    }) || {};
  }

  tableExportContainer = React.createRef();
  render() {
    const {
      page,
      rowsPerPage,
      hideChannelFields,
      isCustomReport,
      inpdf = false,
      fileName,
      isGrandTotalDisplay,
      itemIndex,
      tableColumns
    } = this.props;
    const dataSet = this.prepData();
    const isAdCostDisabled = get(dataSet, 'summaryData.avgAdCost') == null;
    const headers = Object.values(tableColumns).map(item => {
      const { key } = item;
      if (key === 'ads') {
        return {
          ...item,
          icon: (
            <Tooltip title={ADSICON_TOOLTIP_TEXT}>
              <InfoIcon className={styles.adsIcon}/>
            </Tooltip>
          )
        }
      } else {
        return item;
      }
    });

    return (
      <div>
        {
          !inpdf && (
            <ModuleExport
            exportImg={this.tableExportContainer}
            data={this.prepCsvData(dataSet.tableData, dataSet.summaryData, tableColumns)}
            fileName={fileName}
            exportContainerStyle={{padding: '0 20px 0 0'}}
          />
          )
        }
        <div data-test="channelTable" className={styles.tableContainer} ref={this.tableExportContainer}>
          <BaseDataTable 
            data={dataSet.tableData}
            tableHead={<TableHead headers={headers} hideChannelFields={hideChannelFields}/>}
            tableRow={
              <TableRow
                isAdCostDisabled={isAdCostDisabled}
                summaryData={dataSet.summaryData}
                hideChannelFields={hideChannelFields}
                isCustomReport={isCustomReport}
                tableColumns={tableColumns}
              />
            }
            tableSummary={<TableSummary hideChannelFields={hideChannelFields} summaryData={dataSet.summaryData} tableColumns={tableColumns}/>}
            onBodyCellClick={this.handleCellClick}
            initialSortStatus={this.initialSortStatus}
            pagination
            //TODO: revisit those when new report is ready
            inpdf={inpdf}
            page={page}
            rowsPerPage={rowsPerPage}
            isGrandTotalDisplay={isGrandTotalDisplay}
            tableIndex={itemIndex}
            tableId={TRAFFIC_CHANNEL_CHART_ID}
            isCustomReport={isCustomReport}
          />
        </div>
      </div>
    )
  }
}

import { connect } from 'react-redux';
import { getTableSortSelector } from '../../Report/helper';
import { INIT_NEW_AD_USERS_PER_AD_SORT, INIT_NEW_AD_USERS_SORT } from '../constants';

function mapStateToProps(state, props) {
    return {
        tableSortSelector: getTableSortSelector(state, props, TRAFFIC_CHANNEL_CHART_ID),
    }
}

export default connect(mapStateToProps)(ChannelsTable);
